import { useCallback, useEffect, useRef } from 'react'
import { InspectorLayersTreeNode, Point } from '../types'
import { useIsCameraMoving } from '@sketch-hq/sketch-web-renderer'
import { useFindLayerNodeFromCanvasRelativePositionInScreenSpace } from '../useFindLayerNodeFromCanvasRelativePositionInScreenSpace'
import { findPointRelativeToCanvasInScreenSpaceFromMouseEvent } from './findPointRelativeToCanvasInScreenSpaceFromMouseEvent'

/**
 * Hook creating the handler for the mousemove events on the canvas.
 * It also watches for camera movements and recompute the hovered position
 * if the scene has changed after a pan or zoom from the user.
 */
export function useCanvasMouseMoveHandler(
  /** This should only be enabled when the inspector is open and ready */
  isEnabled: boolean,
  inspectorLayersTreeRootNode: InspectorLayersTreeNode | null,
  onMouseMove: (node: InspectorLayersTreeNode | null) => void,
  doesLayerNodeAllowUserInteraction?: (
    node: InspectorLayersTreeNode,
    event: React.MouseEvent<HTMLElement> | undefined
  ) => boolean
) {
  /**
   * Store the last mouse position so we can reuse it to recalculate the hovered layer
   * if the user moves the camera. This is needed for example when the user scrolls,
   * making the scene move behind the cursor without moving it. With the scene moved,
   * another layer is likely to be hovered.
   */
  const lastMousePositionRelativeToCanvasInScreenSpaceRef = useRef<Point | null>(
    null
  )

  const isCameraMoving = useIsCameraMoving()
  const findTargetLayerFromCanvasRelativePositionInScreen = useFindLayerNodeFromCanvasRelativePositionInScreenSpace(
    inspectorLayersTreeRootNode
  )

  const mouseMoveHandler = useCallback(
    (reactEvent: React.MouseEvent<HTMLElement>) => {
      const pointRelativeToCanvasInScreenSpace = findPointRelativeToCanvasInScreenSpaceFromMouseEvent(
        reactEvent
      )

      if (!pointRelativeToCanvasInScreenSpace) {
        return null
      }

      lastMousePositionRelativeToCanvasInScreenSpaceRef.current = pointRelativeToCanvasInScreenSpace
      // Save resources but not recomputed the selected node while user
      // is zooming or panning the camera.
      if (isCameraMoving) {
        return null
      }
      const matchingNode = findTargetLayerFromCanvasRelativePositionInScreen(
        pointRelativeToCanvasInScreenSpace,
        node => doesLayerNodeAllowUserInteraction?.(node, reactEvent) ?? true
      )
      onMouseMove(matchingNode)
    },
    [
      doesLayerNodeAllowUserInteraction,
      findTargetLayerFromCanvasRelativePositionInScreen,
      isCameraMoving,
      onMouseMove,
    ]
  )

  // Recompute the hovered node before and after the camera has moved.
  useEffect(() => {
    if (!isEnabled) {
      // Don't run the hook if we are not on the inspector view
      return
    }
    // Wait for camera to be stable again
    if (isCameraMoving) {
      // Nothing is hovered while the camera is moving.
      // We need to reset the hovered node to null to avoid the old hovered node
      // being marked as hovered for a split second when the camera stops moving
      // and a new layer is hovered because of the camera pan.
      onMouseMove(null)
      return
    }

    const pointRelativeToCanvasInScreenSpace =
      lastMousePositionRelativeToCanvasInScreenSpaceRef.current

    if (!pointRelativeToCanvasInScreenSpace) {
      return
    }

    const matchingNode = findTargetLayerFromCanvasRelativePositionInScreen(
      pointRelativeToCanvasInScreenSpace,
      node => {
        return doesLayerNodeAllowUserInteraction?.(node, undefined) ?? true
      }
    )

    onMouseMove(matchingNode)
  }, [
    doesLayerNodeAllowUserInteraction,
    findTargetLayerFromCanvasRelativePositionInScreen,
    isCameraMoving,
    isEnabled,
    onMouseMove,
  ])

  return mouseMoveHandler
}
